import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import BookingWidget from "./BookingWidget";
import { mediaUrl } from "../../lib/api";
import { useLang, pick } from "../../lib/i18n";

export default function Hero({ settings, slides }) {
  const { lang } = useLang();
  const active = (slides || []).filter((s) => s.active !== false);
  const isVideo = settings?.hero_mode === "video" && !!settings?.hero_video_url;
  const [index, setIndex] = useState(0);
  const [captionOpen, setCaptionOpen] = useState(false);
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 0.6], [0, 140]);

  useEffect(() => {
    if (isVideo || active.length < 2) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % active.length);
      setCaptionOpen(false);
    }, 6500);
    return () => clearInterval(t);
  }, [active.length, isVideo]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative bg-[#FBF9F5]">
      <div className="relative h-[100svh] min-h-[680px] overflow-hidden bg-[#1E1A17]">
        <motion.div style={{ y }} className="absolute inset-0">
          {isVideo ? (
            <video
              data-testid="hero-video"
              className="w-full h-full object-cover"
              src={mediaUrl(settings.hero_video_url)}
              poster={mediaUrl(settings.hero_video_poster) || undefined}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : (
            active.map((s, i) => (
              <motion.div
                key={s.id || i}
                onClick={() => s.image_caption && setCaptionOpen((value) => !value)}
                className="absolute inset-0 cursor-pointer"
                initial={false}
                animate={{ opacity: i === index ? 1 : 0 }}
                transition={{ duration: 1.4, ease: "easeInOut" }}
              >
                <motion.img
                  src={mediaUrl(s.image_url)}
                  alt={s.image_alt || pick(s, "title", lang) || "Nusadira Lembongan Villa"}
                  title={s.image_title || pick(s, "title", lang) || undefined}
                  aria-description={s.image_description || pick(s, "subtitle", lang) || undefined}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: s.image_focal_point || "50% 0%" }}
                  initial={false}
                  animate={{ scale: i === index ? 1 : 1.12 }}
                  transition={{ duration: 7, ease: "linear" }}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              </motion.div>
            ))
          )}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(30,26,23,0.10) 0%, rgba(30,26,23,0.0) 45%, rgba(30,26,23,0.22) 100%)",
            }}
          />
          {captionOpen && active[index]?.image_caption && (
            <div data-testid="hero-photo-caption" className="absolute bottom-20 right-5 sm:right-8 z-10 max-w-[75%] sm:max-w-sm rounded-sm bg-[#1E1A17]/70 backdrop-blur-sm px-4 py-2 text-xs sm:text-sm text-[#F4EFEA] font-sans text-right">
              {active[index].image_caption}
            </div>
          )}
        </motion.div>

        {!isVideo && active.length > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="absolute bottom-7 right-6 sm:right-8 z-10 flex items-center gap-2 opacity-50 hover:opacity-90 transition-opacity duration-500"
          >
            {active.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-dot-${i}`}
                onClick={() => {
                  setIndex(i);
                  setCaptionOpen(false);
                }}
                aria-label={`Slide ${i + 1}`}
                className={`h-[3px] rounded-full transition-all duration-500 ${
                  i === index ? "w-5 bg-white/80" : "w-2 bg-white/35 hover:bg-white/60"
                }`}
              />
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute z-20 bottom-16 left-0 right-0 px-4 sm:px-8"
        >
          <div className="max-w-4xl mx-auto">
            <BookingWidget bookingUrl={settings?.booking_engine_url} />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
